import React, { useState } from 'react';

import ActionButton from '~components/atoms/ActionButton';
import Table from '~components/molecules/Table';
import ViewSerialNoModalModal from '~components/molecules/ViewSerialNoModal';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { GoldConversionStatusEnum, MetalTypeEnum, ModuleEnum, PermissionsEnum } from '~utils/enums';

import UtilFunctions from '~utils/functions';
import combinedActions from '~redux/combinedActions';

import * as Styles from './_goldConversionApprovalTable.module.scss';
import { TUpdateGoldConversionStatusReduxParams } from '~types/feat/approval';
const GoldConversionApprovalTable = () => {
    const dispatch = useAppDispatch();

    const {
        Misc: {
            dateTimeFormat,
        },
    } = UtilFunctions;

    const pendingGoldConversionListing = useAppSelector(state => state.approval.pendingGoldConversionListing.data);
    const isLoading = useAppSelector(state => state.approval.loadings.getPendingGoldConversionListing);

    const access = useAppSelector(state => state.auth.userDetails.access);
    const hasEditAccess = access.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.Approval);

    const [serialNoList, setSerialNoList] = useState<string[]>([]);
    const [isSerialNoModalOpen, setIsSerialNoModalOpen] = useState(false);

    const setTempUpdateApprovalState = (data: TUpdateGoldConversionStatusReduxParams) => dispatch(combinedActions.approvalSetTempUpdateGoldConversionState(data));
    const setConfirmUpdateStatusModalIsOpen = (open: boolean) => dispatch(combinedActions.approvalSetConfirmUpdateGoldConversionStatusModalIsOpen(open));

    const handleApproveClick = (data: TUpdateGoldConversionStatusReduxParams) => {
        setTempUpdateApprovalState(data);
        setConfirmUpdateStatusModalIsOpen(true);
    };

    const handleRejectClick = (data: TUpdateGoldConversionStatusReduxParams) => {
        setTempUpdateApprovalState(data);
        setConfirmUpdateStatusModalIsOpen(true);
    };

    const tableHeader = [
        'TRANSACTION ID',
        'TRANSACTION TYPE',
        'CUSTOMER NAME',
        'CUSTOMER ID NUMBER',
        'CIF NUMBER',
        'BRANCH CODE',
        'BRANCH NAME',
        'REGION NAME',
        'PRODUCT NAME',
        'QUANTITY',
        'SERIAL NUMBER',
        'SALES PERSONNEL NAME',
        'REFERRAL NAME',
        'TRANSACTION DATE',
        'ACTION',
    ];

    const tableData = pendingGoldConversionListing.map(conversion => {
        const {
            conversionId,
            customerName,
            status,
            idNo,
            CIFNo,
            branchCode,
            branchName,
            regionName,
            targetProductName,
            amount,
            serialNo,
            salesPersonnelName,
            referralName,
            createdAt,
            metalType,
        } = conversion;

        const transactionTypeLabel = Number(metalType) === MetalTypeEnum.Silver
            ? 'Silver Conversion'
            : 'Gold Conversion';

        const handleViewSerialNo = () => {
            setIsSerialNoModalOpen(true);
            setSerialNoList(serialNo);
        };

        const renderSerialNo = () => (
            <ActionButton
                key={conversionId}
                label='View'
                onClick={handleViewSerialNo}
            />
        );

        const renderActionButton = () => (
            <div
                key={conversionId}
                className={Styles['action-button-container']}
            >
                {hasEditAccess && (
                    <ActionButton
                        label='Approve'
                        onClick={() =>
                            handleApproveClick({
                                id: conversionId,
                                status: GoldConversionStatusEnum.Approved,
                            })
                        }
                        className={`${Styles['action-button']} ${Styles.approve}`}
                    />
                )}

                {hasEditAccess && (
                    <ActionButton
                        label='Reject'
                        onClick={() =>
                            handleRejectClick({
                                id: conversionId,
                                status: GoldConversionStatusEnum.Rejected,
                            })
                        }
                        className={`${Styles['action-button']} ${Styles.reject}`}
                    />
                )}
            </div>
        );

        return {
            className: Number(metalType) === MetalTypeEnum.Gold
                ? Styles['gold-row']
                : '',
            cells: [
                conversionId,
                transactionTypeLabel,
                customerName,
                idNo,
                CIFNo,
                branchCode,
                branchName,
                regionName,
                targetProductName,
                amount,
                renderSerialNo(),
                salesPersonnelName || '-',
                referralName || '-',
                dateTimeFormat(createdAt),
                status === GoldConversionStatusEnum.Pending
                    ? renderActionButton()
                    : '-',
            ],
        };
    });

    return (
        <>
            <Table
                header={tableHeader}
                body={tableData}
                isLoading={isLoading}
            />

            <ViewSerialNoModalModal
                isOpen={isSerialNoModalOpen}
                onClose={() => setIsSerialNoModalOpen(false)}
                serialNo={serialNoList}
            />
        </>
    );
};

export default GoldConversionApprovalTable;
